import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE } from "@common/constants/transactions";
import { chunk } from "@common/helpers/collections";
import {
    BulkUpdatePayloadInput,
    BulkUpdateRuleApplicationInput,
    BulkUpdateTransactionsRequestInput,
    TransactionFilters,
} from "@common/schemas/transaction.schema";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import { extractRowActionValue, RowActionValue } from "./seedActionRow";
import { ActionRowDefinition } from "./types";

function buildPayloadFieldForValue(
    update: BulkUpdateParams["update"],
    value: RowActionValue,
): BulkUpdatePayloadInput | undefined {
    switch (update) {
        case "categoryId":
            return typeof value === "string"
                ? { categoryId: value }
                : undefined;
        case "coa":
            return typeof value === "string" || value === null
                ? { accountId: value }
                : undefined;
        case "accrualCoa":
            return typeof value === "string" || value === null
                ? { accrualAccountId: value }
                : undefined;
        case "entity":
            return typeof value === "number" ? { entityId: value } : undefined;
        case "classes":
            return Array.isArray(value) ? { classes: value } : undefined;
        case "counterpartyId":
            return typeof value === "string" || value === null
                ? { counterpartyId: value }
                : undefined;
        case "memo":
            return typeof value === "string" ? { memo: value } : undefined;
        default:
            return undefined;
    }
}

function updateMatchesCurrentFilters(
    update: BulkUpdateParams["update"],
    filters: TransactionFilters,
): boolean {
    switch (update) {
        case "categoryId":
            return !!filters.category?.length;
        case "classes":
            return filters.classes !== undefined;
        case "counterpartyId":
            return (
                !!filters.counterpartiesIds?.length ||
                Boolean(filters.withEmptyOrUnverifiedCounterparty)
            );
        case "coa":
            return !!filters.coaAccounts?.length;
        case "accrualCoa":
            return (
                !!filters.accrualCoaAccounts?.length ||
                Boolean(filters.includeUnassignedAccrualAccount)
            );
        case "entity":
            return !!filters.entityIds?.length;
        default:
            return false;
    }
}

function prepareFilterPayloads({
    payload,
    filters,
    update,
    transactionCount,
}: {
    payload: BulkUpdatePayloadInput;
    filters: TransactionFilters;
    update: BulkUpdateParams["update"];
    transactionCount: number;
}): BulkUpdateTransactionsRequestInput[] {
    const pages = Math.ceil(
        transactionCount / DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
    );
    const shouldFetchFromFirstPage = updateMatchesCurrentFilters(
        update,
        filters,
    );

    return Array.from({ length: pages }, (_, page) => ({
        payload,
        options: {
            filters,
            page: shouldFetchFromFirstPage ? 0 : page,
            limit: DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
        },
    }));
}

interface BuildPayloadsParams {
    rows: ActionRowDefinition[];
    filters: TransactionFiltersDto;
    valueToAssign: BulkUpdateParams;
    totalTransactions: number;
    /**
     * Map of row id → list of transaction ids the row currently owns,
     * derived by `ActionsContext` from `newValuesMap` + selected transactions.
     * Non-base rows emit per-id payloads from this bucket.
     */
    rowIdToOwnedTxIds: Map<string, number[]>;
    ruleApplicationsByRowId?: Map<string, BulkUpdateRuleApplicationInput[]>;
}

function withRuleApplications({
    payload,
    rowId,
    ruleApplicationsByRowId,
}: {
    payload: BulkUpdatePayloadInput;
    rowId: string;
    ruleApplicationsByRowId?: Map<string, BulkUpdateRuleApplicationInput[]>;
}): BulkUpdatePayloadInput {
    const ruleApplications = ruleApplicationsByRowId?.get(rowId);
    if (!ruleApplications?.length) {
        return payload;
    }
    return {
        ...payload,
        ruleApplications,
    };
}

/**
 * Builds the per-action bulk update payload list.
 *
 * Strategy mirrors the previous two-phase approach: the base row (if still
 * present) fans out via a filter-based batch covering everyone, then each
 * non-base row issues per-id payloads for the transactions it owns so its
 * value "wins" when processed after the filter batch.
 *
 * Rows with no owned transactions are skipped (they're rule-only changes).
 */
export function buildActionsPayloads({
    rows,
    filters,
    valueToAssign,
    totalTransactions,
    rowIdToOwnedTxIds,
    ruleApplicationsByRowId,
}: BuildPayloadsParams): BulkUpdateTransactionsRequestInput[] {
    const payloads: BulkUpdateTransactionsRequestInput[] = [];

    const baseRow = rows.find((r) => r.isBaseAction);
    if (baseRow) {
        const baseValue = extractRowActionValue(valueToAssign.update, baseRow);
        if (baseValue !== undefined) {
            const payload = buildPayloadFieldForValue(
                valueToAssign.update,
                baseValue,
            );
            if (payload) {
                payloads.push(
                    ...prepareFilterPayloads({
                        payload: withRuleApplications({
                            payload,
                            rowId: baseRow.id,
                            ruleApplicationsByRowId,
                        }),
                        filters,
                        update: valueToAssign.update,
                        transactionCount: totalTransactions,
                    }),
                );
            }
        }
    }

    for (const row of rows) {
        if (row.isBaseAction) {
            continue;
        }
        const value = extractRowActionValue(valueToAssign.update, row);
        if (value === undefined) {
            continue;
        }
        const field = buildPayloadFieldForValue(valueToAssign.update, value);
        if (!field) {
            continue;
        }

        if (row.filterScope) {
            payloads.push(
                ...prepareFilterPayloads({
                    payload: withRuleApplications({
                        payload: field,
                        rowId: row.id,
                        ruleApplicationsByRowId,
                    }),
                    filters: row.filterScope.filters,
                    update: valueToAssign.update,
                    transactionCount: row.filterScope.transactionCount,
                }),
            );
            continue;
        }

        const ownedIds = rowIdToOwnedTxIds.get(row.id) ?? [];
        if (ownedIds.length === 0) {
            continue;
        }
        const chunks = chunk(
            ownedIds,
            DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
        );
        for (const ids of chunks) {
            const payload = withRuleApplications({
                payload: field,
                rowId: row.id,
                ruleApplicationsByRowId,
            });
            payloads.push({
                transactions: ids.map((id) => ({
                    transactionId: id,
                    ...payload,
                })),
            });
        }
    }

    return payloads;
}
